import { attachShopify, type ShopifyPageSignals } from "./auto/shopify.js";
import { attachWooCommerce, type WooPageSignals } from "./auto/woocommerce.js";
import { PixelClient } from "./client.js";
import { cookieStore } from "./storage.js";
import type { Consent, PixelConfig } from "./types.js";

/**
 * Script-tag entry, bundled to an IIFE (`Retarget` global) by esbuild. The platform plugin
 * enqueues the bundle and injects RetargetConfig, then auto-capture runs for the detected
 * platform. Also exposes window.Retarget for manual `track(...)` calls from theme code.
 */
export interface RetargetBoot extends PixelConfig {
  signals?: WooPageSignals | ShopifyPageSignals;
}

declare global {
  interface Window {
    RetargetConfig?: RetargetBoot;
    Retarget?: PublicApi;
  }
}

export interface PublicApi {
  client: PixelClient;
  track(
    event: Parameters<PixelClient["track"]>[0],
    properties?: Record<string, unknown>,
    consent?: Consent,
  ): Promise<boolean>;
}

export function boot(boot: RetargetBoot): PublicApi {
  const client = new PixelClient(boot, cookieStore());
  const api: PublicApi = {
    client,
    track: (event, properties = {}, consent) =>
      client.track(event, properties, { url: location.href, referrer: document.referrer, nowMs: Date.now() }, consent),
  };

  const run = (): void => {
    if (boot.platform === "shopify") {
      const signals: ShopifyPageSignals = (boot.signals as ShopifyPageSignals | undefined) ?? {
        product: null,
        collection: null,
        isCart: false,
        isCheckout: false,
        cartUpdateUrl: "/cart/update.js",
      };
      attachShopify(client, signals);
    } else {
      const signals: WooPageSignals = (boot.signals as WooPageSignals | undefined) ?? {
        product: null,
        collection: null,
        isCart: false,
        isCheckout: false,
      };
      attachWooCommerce(client, signals);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
  return api;
}
